import { useState } from 'react';

import { useMediaQuery } from '@mui/material';

import { CreateCtaModal } from '@src/features';
import { QuestionConfigModal } from './partials';
import CTAManagementModal from '@src/features/cta/partials/CTAManagementModal';
import { OButton, OCalendar, OCheck, OInput, OResponsiveModal, OSelect, OSwitch, OTooltip } from '@src/shared';

import { isWebView } from '@src/helpers';

import { useBridgeModal } from '@src/hooks/useBridgeModal';
import useWebinarForm from './useWebinarForm';

import { CURRENT_TIME_ZONE } from '@src/constants';
import timezoneModalStyles from './timeZoneModal.module.scss';

interface IOwnProps {
  submitLabel?: string;
}

const WebinarForm = ({ submitLabel = 'Create' }: IOwnProps) => {
  const {
    updateFormField,
    onDiscard,
    removeFormError,
    setFormErrors,
    debouncedSubmitFunction: onSubmit,
    handleKeyDown: onKeyDown,
    isTimeZoneModalOpen,

    formState,
    durationValues,
    formErrors,
    setIsTimeZoneModalOpen,
    formattedTime,
    selectedTimeZone,
    setSelectedTimeZone,
    timeZones,
    isMaxLimitEnabled,
    isQuestionsModalOpened,
    setIsQuestionsModalOpened,
    backupQuestionSettings,
    isLoading,
    handleMaxQuestionsToggle,
    onQuestionsCancel,
    onQuestionsDone,
    handleOpenCTAManagement,
    isDateEditable,
    isForbidden,
  } = useWebinarForm();

  const isSubmitDisabled = isLoading || isForbidden;
  const isMobile = useMediaQuery('(max-width:768px)');

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useBridgeModal(isCalendarOpen);

  const onTimeZoneClose = () => {
    setIsTimeZoneModalOpen(false);
    setSelectedTimeZone(formState.timeZone);
    updateFormField('timeZone', selectedTimeZone);
  };

  const onUseCurrent = () => {
    updateFormField('timeZone', CURRENT_TIME_ZONE);
    setSelectedTimeZone(CURRENT_TIME_ZONE);
    setIsTimeZoneModalOpen(false);
  };

  return (
    <>
      <form
        className="webinar-form"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(e);
        }}
        onKeyDown={onKeyDown}
      >
        <div className="webinar-form--topic">
          <label>Topic</label>
          <OInput
            value={formState.title}
            onChange={(event) => {
              updateFormField('title', event.target.value);
              removeFormError('title');
            }}
            className="webinar-form-input"
            invalid={!!formErrors.title}
          />
          {formErrors.title && <p className="invalid">{formErrors.title}</p>}
        </div>
        <div className={`webinar-form-time-container ${isMobile ? 'mobile' : ''}`}>
          <OTooltip
            title={
              !isDateEditable ? 'Webinar lobby already started, you won’t be able to edit webinar time' : undefined
            }
          >
            <div style={!isDateEditable ? { cursor: 'not-allowed' } : undefined}>
              <div
                className="webinar-form-time"
                style={!isDateEditable ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
              >
                <div className="calendar-container">
                  <div className="label-container">
                    <label>Date and time</label>
                    {isMobile && (
                      <div className="timeZone-container">
                        <OButton
                          text="Time zone"
                          isDisabled={!isDateEditable}
                          onClick={(e) => {
                            e.preventDefault();
                            setIsTimeZoneModalOpen(true);
                          }}
                          classes={{
                            container: 'button-container',
                          }}
                          size="x-medium"
                        />
                      </div>
                    )}
                  </div>
                  <OCalendar
                    value={formState.scheduledDate}
                    onChange={(e) => {
                      updateFormField('scheduledDate', e.value);
                      removeFormError('scheduledDate');
                    }}
                    dateFormat="EEE, dd MMM, yyyy hh:mm a"
                    classes={{ invalid: formErrors.scheduledDate ? 'invalid' : '' }}
                    calendarProps={{
                      minDate: new Date(),
                      disabled: !isDateEditable,
                      panelClassName: 'webinar-form-calendar',
                    }}
                    onShow={() => {
                      setIsCalendarOpen(true);
                    }}
                    onHide={() => {
                      setIsCalendarOpen(false);
                    }}
                  />

                  {formErrors.scheduledDate && <p className="invalid">{formErrors.scheduledDate}</p>}
                </div>

                <div className="duration-container">
                  <label>Duration</label>
                  <OSelect
                    items={durationValues}
                    selectedValue={formState.durationValue}
                    isDisabled={!isDateEditable}
                    classes={{
                      customTextSize: 'custom-text-size',
                      customBorderColor: 'var(--primary)',
                      customMenuBackground: 'custom-menu-background',
                      customInput: 'custom-input',
                    }}
                    alignItems="start"
                    onChange={(e) => updateFormField('durationValue', e)}
                    size="full"
                  />
                </div>
                {!isMobile && (
                  <div className="timeZone-container">
                    <OButton
                      text="Time zone"
                      isDisabled={!isDateEditable}
                      onClick={(e) => {
                        e.preventDefault();
                        setIsTimeZoneModalOpen(true);
                      }}
                      classes={{
                        container: 'button-container',
                      }}
                      size="x-medium"
                    />
                  </div>
                )}
              </div>
            </div>
          </OTooltip>
          <span className="webinar-formatted-time">{formattedTime}</span>
        </div>
        <div className="webinar-form--settings">
          <label>Webinar settings</label>
          <OSwitch
            classes={{ border: 'none', alignment: 'left', container: 'custom-background' }}
            label="Attendees are required to register before joining this webinar meeting."
            size="full"
            variant="primary"
            isChecked={formState.requireRegistration}
            onChange={(checked) => updateFormField('requireRegistration', checked)}
          />

          {formState.requireRegistration && (
            <div className="settings-require-register ">
              <OCheck
                checked={formState.expiresAfterEnd}
                onChange={(checked) => updateFormField('expiresAfterEnd', checked)}
                label="Add an expiry date to the registration"
              />

              {formState.expiresAfterEnd && (
                <div className="expire-calendar">
                  <OCalendar
                    value={formState.registerExpireDate}
                    onChange={(e) => {
                      updateFormField('registerExpireDate', e.value);
                      removeFormError('registerExpireDate');
                    }}
                    dateFormat="dd MMM, yyyy hh:mm a"
                    classes={{ invalid: formErrors.registerExpireDate ? 'invalid' : '' }}
                    calendarProps={{
                      minDate: new Date(),
                      maxDate: formState.scheduledDate
                        ? new Date(formState.scheduledDate.getTime() - 60 * 1000)
                        : new Date(),
                      panelClassName: 'webinar-form-calendar',
                    }}
                    onShow={() => {
                      setIsCalendarOpen(true);
                    }}
                    onHide={() => {
                      setIsCalendarOpen(false);
                    }}
                  />

                  {formErrors.registerExpireDate && <p className="invalid">{formErrors.registerExpireDate}</p>}
                </div>
              )}
            </div>
          )}

          <div className="settings-enable-features">
            <OSwitch
              classes={{ border: 'none', alignment: 'left', container: 'custom-background' }}
              label="Enable Q&A engagement"
              size="fit"
              variant="primary"
              isChecked={formState.questionsEnabled}
              onChange={(checked) => updateFormField('questionsEnabled', checked)}
            />
            <OButton
              text="Advanced Settings"
              onClick={(e) => {
                e.preventDefault();
                if (!formState.questionsEnabled) return;
                backupQuestionSettings.current = {
                  questionsEnabled: formState.questionsEnabled,
                  maxQuestions: formState.maxQuestions,
                  allowAnonymous: formState.allowAnonymous,
                  allowDismissQuestion: formState.allowDismissQuestion,
                  maxThreadReplies: formState.maxThreadReplies,
                  isMaxLimitEnabled: isMaxLimitEnabled,
                };
                setIsQuestionsModalOpened(true);
              }}
              isTextButton={!formState.questionsEnabled}
              classes={{
                container: `button-container`,
              }}
              size={'x-medium'}
            />
          </div>

          <div className="settings-enable-features">
            <OSwitch
              classes={{ border: 'none', alignment: 'left', container: 'custom-background' }}
              label="Enable Call to Action Popup"
              size="fit"
              variant="primary"
              isChecked={formState.ctaEnabled}
              onChange={(checked) => updateFormField('ctaEnabled', checked)}
            />
            <OButton
              text="CTA Management"
              onClick={(e) => {
                e.preventDefault();
                if (!formState.ctaEnabled) return;
                handleOpenCTAManagement();
              }}
              isTextButton={!formState.ctaEnabled}
              classes={{
                container: `button-container`,
              }}
              size={'x-medium'}
            />
          </div>

          <div className="settings-enable-features">
            <OSwitch
              classes={{ border: 'none', alignment: 'left', container: 'custom-background' }}
              label="Enable calculator"
              size="fit"
              variant="primary"
              isChecked={formState.calculatorEnabled}
              onChange={(checked) => updateFormField('calculatorEnabled', checked)}
            />
          </div>
        </div>

        <div className="webinar-form--settings">
          <label>Webinar reminders</label>

          <OSwitch
            classes={{ border: 'none', alignment: 'left', container: 'custom-background' }}
            label="Customize Webinar Reminder Settings"
            size="full"
            variant="primary"
            isChecked={(formState.reminders?.length ?? 0) > 0}
            onChange={(checked) => {
              if (!checked) {
                updateFormField('reminders', []);
              } else if ((formState.reminders?.length ?? 0) === 0) {
                updateFormField('reminders', [null]);
              }
            }}
          />

          {(formState.reminders ?? []).length > 0 ? (
            <>
              <div className="expire-calendar-reminder">
                <p className="reminder-text">Send email reminder of the webinar schedule on:</p>
                <div>
                  <OCalendar
                    value={formState.reminders?.[0] ?? null}
                    onChange={(e) => {
                      const updated = [...(formState.reminders ?? [])];
                      updated[0] = e.value;
                      updateFormField('reminders', updated);
                      removeFormError('reminders_0');
                    }}
                    dateFormat="dd MMM, yyyy hh:mm a"
                    classes={{ invalid: formErrors?.['reminders_0'] ? 'invalid' : '' }}
                    calendarProps={{
                      minDate: new Date(),
                      maxDate: formState.scheduledDate
                        ? new Date(formState.scheduledDate.getTime() - 20 * 60000)
                        : new Date(),
                      panelClassName: 'webinar-form-calendar',
                    }}
                    onShow={() => {
                      setIsCalendarOpen(true);
                    }}
                    onHide={() => {
                      setIsCalendarOpen(false);
                    }}
                  />
                  {formErrors?.['reminders_0'] && <p className="invalid">{formErrors['reminders_0']}</p>}
                </div>
              </div>

              <div className="expire-calendar-reminder second-reminder">
                <OCheck
                  checked={(formState.reminders?.length ?? 0) > 1}
                  onChange={(checked) => {
                    const updated = [...(formState.reminders ?? [])];
                    if (checked) {
                      updated[1] = null;
                    } else {
                      updated.splice(1, 1);
                    }
                    updateFormField('reminders', updated);
                  }}
                  className="reminder-text"
                  label="Set another email reminder of the webinar schedule on:"
                />

                {formState.reminders?.[1] !== undefined && (
                  <div>
                    <OCalendar
                      value={formState.reminders?.[1]}
                      onChange={(e) => {
                        const updated = [...(formState.reminders ?? [])];
                        updated[1] = e.value;
                        updateFormField('reminders', updated);
                        removeFormError('reminders_1');
                      }}
                      dateFormat="dd MMM, yyyy hh:mm a"
                      classes={{ invalid: formErrors?.['reminders_1'] ? 'invalid' : '' }}
                      calendarProps={{
                        minDate: new Date(),
                        maxDate: formState.scheduledDate
                          ? new Date(formState.scheduledDate.getTime() - 20 * 60000)
                          : new Date(),
                        panelClassName: 'webinar-form-calendar',
                      }}
                      onShow={() => {
                        setIsCalendarOpen(true);
                      }}
                      onHide={() => {
                        setIsCalendarOpen(false);
                      }}
                    />
                    {formErrors?.['reminders_1'] && <p className="invalid">{formErrors['reminders_1']}</p>}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="system-default-reminder">
              The system automatically sends a reminder 48 hours before the webinar
            </p>
          )}
        </div>

        <div className="action-buttons-container">
          {!isWebView() && <OButton onClick={onDiscard} text="Discard" outlined size="x-medium" />}
          <OTooltip
            title={isForbidden ? 'Webinar lobby already started, you won’t be able to edit webinar' : undefined}
          >
            <div>
              <OButton
                isDisabled={isSubmitDisabled}
                text={submitLabel}
                variant="alternative"
                size="x-medium"
                type="submit"
              />
            </div>
          </OTooltip>
        </div>
      </form>
      <OResponsiveModal
        isOpen={isTimeZoneModalOpen}
        onClose={onTimeZoneClose}
        title="Webinar time zone"
        modalClasses={{ container: timezoneModalStyles.timezoneModalContainer }}
        drawerActions={{
          onApply: onTimeZoneClose,
        }}
        hasBackdrop={true}
      >
        <div className={`${timezoneModalStyles.timezoneModalContainerDrawer}`}>
          <label className={timezoneModalStyles.label}>Time zone</label>
          <OSelect
            items={timeZones}
            selectedValue={selectedTimeZone}
            onChange={(selectedValue) => setSelectedTimeZone(selectedValue)}
            size="full"
            alignItems="start"
          />

          <div className={timezoneModalStyles.currentTimeZone} onClick={onUseCurrent}>
            <span>Use Current Timezone</span>
          </div>
          {!isMobile && <OButton text="Done" onClick={onTimeZoneClose} variant="primary" outlined size="x-small" />}
        </div>
      </OResponsiveModal>

      {/*QA Modal*/}
      <QuestionConfigModal
        formErrors={formErrors}
        formState={formState}
        handleMaxQuestionsToggle={handleMaxQuestionsToggle}
        isMaxLimitEnabled={isMaxLimitEnabled}
        isMobile={isMobile}
        isQuestionsModalOpened={isQuestionsModalOpened}
        onQuestionsCancel={onQuestionsCancel}
        onQuestionsDone={onQuestionsDone}
        removeFormError={removeFormError}
        setFormErrors={setFormErrors}
        updateFormField={updateFormField}
      />
      {/* CTA */}
      <CTAManagementModal />
      <CreateCtaModal />
    </>
  );
};

export default WebinarForm;
